/**
 * auth-api.ts - API client for authentication operations
 */

import type {
  User,
  LoginRequest,
  LoginResponse,
  LoginErrorResponse,
  RegisterRequest,
  RegisterResponse,
  PasswordChangeRequest,
  ForgotPasswordRequest,
  ContactFormRequest,
  ResendVerificationRequest,
  ApiResponse
} from '../../types/auth';
import { AUTH_API_BASE } from '../utils/constants';

/**
 * Check current authentication status
 * @returns User object if authenticated, null otherwise
 */
export async function checkAuthStatus(): Promise<User | null> {
  try {
    const response = await fetch(`${AUTH_API_BASE}/me`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      return data.user;
    }
    return null;
  } catch (error) {
    console.error('Error checking auth status:', error);
    return null;
  }
}

/**
 * Login with email and password
 * @param credentials - Login credentials
 * @returns Login response or throws error
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${AUTH_API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(credentials)
  });

  const data = await response.json();

  if (!response.ok) {
    const error = data as LoginErrorResponse;
    throw error;
  }

  return data as LoginResponse;
}

/**
 * Register a new user
 * @param credentials - Registration data
 * @returns Register response or throws error
 */
export async function register(credentials: RegisterRequest): Promise<RegisterResponse> {
  const response = await fetch(`${AUTH_API_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(credentials)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Registration failed');
  }

  return data as RegisterResponse;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  await fetch(`${AUTH_API_BASE}/logout`, {
    method: 'POST',
    credentials: 'include'
  });
}

/**
 * Change password for current user
 * @param passwordData - Current and new password
 * @returns Success message or throws error
 */
export async function changePassword(passwordData: PasswordChangeRequest): Promise<ApiResponse> {
  const response = await fetch(`${AUTH_API_BASE}/password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(passwordData)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update password');
  }

  return data;
}

/**
 * Request password reset email
 * @param email - User's email address
 * @returns Success message or throws error
 */
export async function forgotPassword(email: string): Promise<ApiResponse> {
  const response = await fetch(`${AUTH_API_BASE}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email } as ForgotPasswordRequest)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to send reset email');
  }

  return data;
}

/**
 * Send contact form message
 * @param contactData - Contact form data
 * @returns Success message or throws error
 */
export async function sendContactForm(contactData: ContactFormRequest): Promise<ApiResponse> {
  const response = await fetch(`${AUTH_API_BASE}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contactData)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to send message');
  }

  return data;
}

/**
 * Resend verification email
 * @param email - User's email address
 * @returns Success message or throws error
 */
export async function resendVerification(email: string): Promise<ApiResponse> {
  const response = await fetch(`${AUTH_API_BASE}/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email } as ResendVerificationRequest)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to send verification email');
  }

  return data;
}

/**
 * Update username for current user
 * @param username - New username
 * @returns Updated user data or throws error
 */
export async function updateUsername(username: string): Promise<{ user: User; message: string }> {
  const response = await fetch(`${AUTH_API_BASE}/username`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update username');
  }

  return data;
}
